'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AppImage from '@/components/ui/AppImage';

interface MediaMessageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  hasText: boolean;
  borderRadius: string;
  timestamp?: string;
  isMe?: boolean;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

function StatusIcon({ status }: { status?: MediaMessageProps['status'] }) {
  if (!status) return null;
  const color = status === 'read' ? '#2EA6FF' : 'rgba(255,255,255,0.75)';
  if (status === 'sending' || status === 'sent') {
    return (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  return (
    <svg width="15" height="11" viewBox="0 0 28 12" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="1 6 5 10 12 3" />
      <polyline points="9 6 13 10 20 3" />
    </svg>
  );
}

export default function MediaMessage({ src, alt, width, height, hasText, borderRadius, timestamp, isMe, status }: MediaMessageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  // Compact preview: 160px wide, aspect ratio capped between 0.65 and 1.1
  const aspectRatio = Math.min(1.1, Math.max(0.65, height / width));
  const previewWidth = 160;
  const previewHeight = Math.round(previewWidth * aspectRatio);

  const openViewer = useCallback(() => setIsOpen(true), []);
  const closeViewer = useCallback(() => setIsOpen(false), []);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeViewer(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, closeViewer]);

  // Prevent body scroll when viewer open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const viewer = isOpen && mounted ? createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        background: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100vw',
        height: '100vh',
      }}
      onClick={closeViewer}
      role="dialog"
      aria-modal="true"
      aria-label="Full screen image viewer"
    >
      {/* Image fills the screen */}
      <img
        src={src}
        alt={alt}
        style={{
          maxWidth: '100vw',
          maxHeight: '100vh',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Close button — top left, above everything */}
      <button
        onClick={(e) => { e.stopPropagation(); closeViewer(); }}
        style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.18)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2147483647,
        }}
        aria-label="Close image viewer"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Compact photo preview bubble */}
      <div
        className="relative cursor-pointer overflow-hidden flex-shrink-0"
        style={{
          width: `${previewWidth}px`,
          height: `${previewHeight}px`,
          borderRadius,
        }}
        onClick={openViewer}
        role="button"
        aria-label="View photo"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openViewer(); }}
      >
        {/* Loading skeleton */}
        {!loaded && (
          <div
            className="absolute inset-0 animate-pulse"
            style={{ borderRadius, background: 'rgba(255,255,255,0.08)' }}
            aria-hidden="true"
          />
        )}
        <AppImage
          src={src}
          alt={alt}
          fill
          sizes="320px"
          className={`object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 hover:bg-black/15 transition-colors duration-150" aria-hidden="true" />

        {/* Timestamp overlay — bottom right of photo */}
        {timestamp && (
          <div
            className="absolute bottom-1 right-1 flex items-center gap-0.5 rounded-full px-1.5 py-0.5"
            style={{ background: 'rgba(0,0,0,0.55)' }}
          >
            <span className="text-[10px] text-white leading-none font-medium">{formatTime(timestamp)}</span>
            {isMe && <StatusIcon status={status} />}
          </div>
        )}
      </div>

      {viewer}
    </>
  );
}